import { Box, Paper, Stack } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { GlobalDataContext } from "../../../../contexts/GlobalData";
import { PRESCRIPTION_TEMPLATES_LIST } from "../../../../enums/QueryKeys";
import { fetchPrescriptionTemplates } from "../../../../enums/queryFunction/Fetch";
import { EDIT_PRESCRIPTION_TEMPLATES, DELETE_PRESCRIPTION_TEMPLATES } from "../../../../enums/PermissionTitles";
import useUser from "../../../../hooks/useUser";
import { useSearchParams } from "react-router-dom";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import TemplatesSearchFilter from "../components/TemplatesSearchFilter";
import PrescriptionsTable from "./PrescriptionsTable";
import PrescriptionTemplateDetailsDialog from "./PrescriptionTemplateDetailsDialog";
import PrescriptionTemplateFormDialog from "./PrescriptionTemplateFormDialog";
import DeletePrescriptionTemplateDialog from "./DeletePrescriptionTemplateDialog";

const STRING_SORT_FIELDS = new Set(["title", "manufacturerName", "genericName"]);

/** Mirrors TemplatesTab.jsx exactly, minus the category filter Paper (no category concept for prescription templates). */
function compareTemplates(a, b, orderBy, order) {
  const direction = order === "desc" ? -1 : 1;
  const aValue = a?.[orderBy];
  const bValue = b?.[orderBy];
  if (!aValue && !bValue) return 0;
  if (!aValue) return 1;
  if (!bValue) return -1;
  if (STRING_SORT_FIELDS.has(orderBy)) {
    return direction * String(aValue).localeCompare(String(bValue));
  }
  return direction * (Number(aValue) - Number(bValue));
}

const PrescriptionsTab = React.forwardRef(function PrescriptionsTab(props, ref) {
  const { currentClinic } = useUser();
  const clinicId = currentClinic?.id;
  const [searchQuery, setSearchQuery] = useSearchParams("");
  const titleParam = searchQuery.get("prescriptionQ") ?? "";
  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: [PRESCRIPTION_TEMPLATES_LIST + clinicId, titleParam],
    queryFn: () => fetchPrescriptionTemplates(clinicId, titleParam),
    staleTime: Infinity,
  });
  const queryClient = useQueryClient();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isEditingAllowed = isPermissionAllowed(EDIT_PRESCRIPTION_TEMPLATES);
  const isDeletingAllowed = isPermissionAllowed(DELETE_PRESCRIPTION_TEMPLATES);

  const items = React.useMemo(() => (Array.isArray(templates) ? templates : []), [templates]);
  const isSearchActive = titleParam !== "";

  const prescriptionSortParam = searchQuery.get("prescriptionSort") || "";
  const [orderBy, order] = prescriptionSortParam
    ? prescriptionSortParam.split("=")
    : ["title", "asc"];
  const sortedItems = React.useMemo(
    () => [...items].sort((a, b) => compareTemplates(a, b, orderBy, order)),
    [items, orderBy, order],
  );

  function handleSort(field, direction) {
    updateQueryParam("prescriptionSort", `${field}=${direction}`);
  }

  const [detailsTemplateId, setDetailsTemplateId] = React.useState(null);
  const [formState, setFormState] = React.useState(null);
  const [templatePendingDelete, setTemplatePendingDelete] = React.useState(null);

  const detailsTemplate = items.find((item) => item.id === detailsTemplateId) ?? null;

  React.useImperativeHandle(ref, () => ({
    openAddDialog: () => setFormState({ template: null }),
  }));

  const updateQueryParam = React.useCallback(
    (key, value) => {
      setSearchQuery((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setSearchQuery],
  );

  function closeTemplateForm() {
    setFormState(null);
    queryClient.invalidateQueries({ queryKey: [PRESCRIPTION_TEMPLATES_LIST + clinicId] });
  }

  if (error) {
    return <SimpleAlert message="ErrorFetchingWarehouseItemsData" severity="error" />;
  }

  return (
    <>
      <Stack spacing={2} mb={3}>
        <Box sx={{ display: "flex", width: "100%" }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, flex: 1, borderRadius: 3, borderColor: "divider" }}
          >
            <TemplatesSearchFilter
              initialValue={titleParam}
              onSearchChange={(val) => updateQueryParam("prescriptionQ", val)}
            />
          </Paper>
        </Box>
      </Stack>

      <PrescriptionsTable
        items={sortedItems}
        isLoading={isLoading}
        emptyMessage={isSearchActive ? "NoPrescriptionTemplatesFoundForGivenQuery" : "NoPrescriptionTemplates"}
        onRowClick={(template) => setDetailsTemplateId(template.id)}
        order={order}
        orderBy={orderBy}
        onSort={handleSort}
      />

      <PrescriptionTemplateDetailsDialog
        template={detailsTemplate}
        canEdit={isEditingAllowed}
        canDelete={isDeletingAllowed}
        onClose={() => setDetailsTemplateId(null)}
        onEdit={(template) => {
          setDetailsTemplateId(null);
          setFormState({ template });
        }}
        onDelete={(template) => setTemplatePendingDelete(template)}
      />

      {formState && (
        <PrescriptionTemplateFormDialog
          open={Boolean(formState)}
          onClose={closeTemplateForm}
          clinicId={clinicId}
          initialValues={formState.template}
        />
      )}

      <DeletePrescriptionTemplateDialog
        template={templatePendingDelete}
        clinicId={clinicId}
        onCancel={() => setTemplatePendingDelete(null)}
        onDeleted={() => setDetailsTemplateId(null)}
      />
    </>
  );
});

export default PrescriptionsTab;
